import { createContext, ReactNode, useContext, useMemo } from "react";

export interface RedirectingState {
  url: string;
  isRedirecting: boolean;
  clickEvent?: () => void;
  shouldHardRedirect?: boolean;
}

export interface EditingState {
  isEditing: boolean;
  location: "document-upload" | "conversation" | "customer-settings" | null;
}

export interface NavigationOptions {
  url: string;
  eventTitle?: string;
  e?: React.MouseEvent<HTMLAnchorElement | HTMLDivElement | HTMLLIElement>;
  clickEvent?: () => void;
  shouldHardRedirect?: boolean;
}

interface GlobalContextValue {
  editingState: EditingState;
  setEditingState: (isEditing: EditingState) => void;
  redirectingState: RedirectingState;
  setRedirectingState: (redirecting: RedirectingState) => void;
  handleNavigation: ({
    url,
    eventTitle,
    e,
    clickEvent,
    shouldHardRedirect,
  }: NavigationOptions) => void;
}

interface GlobalContextProps extends GlobalContextValue {
  children: ReactNode;
}

const GlobalContext = createContext<GlobalContextValue | undefined>(undefined);

export function GlobalContextProvider({
  children,
  editingState,
  setEditingState,
  redirectingState,
  setRedirectingState,
  handleNavigation,
}: GlobalContextProps) {
  const value = useMemo(
    () => ({
      editingState,
      setEditingState,
      redirectingState,
      setRedirectingState,
      handleNavigation,
    }),
    [
      editingState,
      setEditingState,
      redirectingState,
      setRedirectingState,
      handleNavigation,
    ],
  );

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
}

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error(
      "useGlobalContext must be used within an GlobalContext.Provider",
    );
  }
  return context;
};
